import { toChildArray, Ref, ContextType } from 'preact';
import { forwardRef } from 'preact/compat';
import { useRef, useState } from 'preact/hooks';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { LiveRegion } from '../UNSAFE_LiveRegion';
import { TestIdProps } from '../hooks/UNSAFE_useTestId';
import { TabBar, TabBarProps } from '../UNSAFE_TabBar';
import { ReorderableTabBarItem, ITEM_SELECTOR } from './ReorderableTabBarItem';
import { ReorderableContext, useReorderable } from '../hooks/PRIVATE_useReorderable';
import { ReorderProps } from '../UNSAFE_Collection/Reorderable.types';
import { TabBarContextMenuConfig } from '../UNSAFE_TabBarCommon';

export type ReorderableTabBarProps<K extends string | number> = Omit<
  TabBarProps<K>,
  'aria-describedby'
> &
  ReorderProps<K> &
  TestIdProps;

type TabBarReorderContextProps = ContextType<typeof ReorderableContext>;
/**
 * A component that allows reordering of tabs within a TabBar.
 */
export const ReorderableTabBar = forwardRef(
  <K extends string | number>(
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      children,
      layout = 'stretch',
      display = 'standard',
      size = 'md',
      edge = 'top',
      selection,
      onSelect,
      onRemove,
      onReorder,
      contextMenuConfig,
      testId
    }: ReorderableTabBarProps<K>,
    ref: Ref<HTMLDivElement>
  ) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const [dragKey, setDragKey] = useState<K | undefined>(undefined);
    const childrenArray = toChildArray(children);

    const displayStackProps = {
      display: display
    };
    const displayNonStackProps = {
      display: display,
      size: size
    };
    const displayProps = display === 'stacked' ? displayStackProps : displayNonStackProps;

    const {
      reorderProps,
      reorderContext,
      reorderInstructionsId,
      reorderStatus,
      reorderInstructions
    } = useReorderable({
      onReorder,
      rootRef,
      itemSelector: ITEM_SELECTOR
    });

    const tabBarReorderContext = {
      currentItemKey: reorderContext.currentItemKey,
      onReorder: onReorder as TabBarReorderContextProps['onReorder']
    };

    return (
      <div ref={rootRef} {...reorderProps}>
        <ReorderableContext.Provider value={tabBarReorderContext}>
          <TabBar
            ref={ref}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            layout={layout}
            edge={edge}
            selection={selection}
            onSelect={onSelect}
            onRemove={onRemove}
            testId={testId}
            {...displayProps}
            aria-describedby={reorderInstructionsId}
            contextMenuConfig={contextMenuConfig as TabBarContextMenuConfig<string | number>}>
            {childrenArray.map((child, idx) => {
              return (
                <ReorderableTabBarItem
                  dragKey={dragKey}
                  setDragKey={setDragKey}
                  onReorder={onReorder}
                  layout={layout}
                  rootRef={rootRef}
                  isFirstItem={idx === 0}
                  isLastItem={idx === childrenArray.length - 1}>
                  {child}
                </ReorderableTabBarItem>
              );
            })}
          </TabBar>
        </ReorderableContext.Provider>
        <span id={reorderInstructionsId}>
          <HiddenAccessible>{reorderInstructions}</HiddenAccessible>
        </span>
        <LiveRegion>{reorderStatus}</LiveRegion>
      </div>
    );
  }
);
